// The live-stream filters as they appear in the pages' addresses and in GET /api/streams:
//   /cams/chaturbate?gender=female&region=south_america&tag=bigboobs&new=1   (a platform's own page)
//   /?gender=female…                                                          (every platform: the home page)
//   /api/streams?platform=chaturbate&gender=female…
// Shared by the page, the API and the filter links, so all three read and write them the same way.

import {
  isStreamGender,
  isStreamRegion,
  normalizeTag,
  platformById,
  type StreamGender,
  type StreamPlatform,
  type StreamRegion,
} from "@/lib/stream-platforms";

/** What the home page can narrow the live streams to (each one optional). */
export interface StreamFilters {
  source?: string;
  gender?: StreamGender;
  region?: StreamRegion;
  /** A normalized tag (normalizeTag). */
  tag?: string;
  isNew?: boolean;
  /** The visitor, for the rooms' location restrictions (lib/streams.ts, viewerRestrictions). */
  viewer?: ViewerRestrictions;
}

/**
 * What about the visitor some live room restricts: their country when a room blocks it ("?" when it
 * isn't known, which hides every room that blocks any country) and those of their languages that a
 * room blocks. Nothing when no room restricts them, so most visitors share one cached list.
 */
export interface ViewerRestrictions {
  country?: string;
  languages?: string[];
}

export interface StreamQuery {
  platform: StreamPlatform | null;
  gender?: StreamGender;
  region?: StreamRegion;
  tag?: string;
  isNew?: boolean;
}

/** Reads the filters; `invalid` names the first parameter that isn't a known value (the page ignores it, the API refuses it). */
export function readStreamQuery(get: (name: string) => string | null | undefined): { query: StreamQuery; invalid?: string } {
  const query: StreamQuery = { platform: null };
  let invalid: string | undefined;
  const platform = get("platform");
  if (platform) {
    query.platform = platformById(platform);
    if (!query.platform) invalid ??= "platform";
  }
  const gender = get("gender");
  if (gender) {
    if (isStreamGender(gender)) query.gender = gender;
    else invalid ??= "gender";
  }
  const region = get("region");
  if (region) {
    if (isStreamRegion(region)) query.region = region;
    else invalid ??= "region";
  }
  const tag = get("tag");
  if (tag) {
    const t = normalizeTag(tag);
    if (t) query.tag = t;
    else invalid ??= "tag";
  }
  if (get("new") === "1") query.isNew = true;
  return { query, invalid };
}

/** The filters of a page's searchParams; unknown values are ignored rather than refused. */
export const searchQuery = (sp: Record<string, string | string[] | undefined>): StreamQuery =>
  readStreamQuery((name) => (typeof sp[name] === "string" ? (sp[name] as string) : undefined)).query;

export const toFilters = (q: StreamQuery): StreamFilters => ({ source: q.platform?.source, gender: q.gender, region: q.region, tag: q.tag, isNew: q.isNew });

/** The query string for these filters, e.g. "platform=chaturbate&tag=bigboobs" (empty when none). */
export function streamParams(q: StreamQuery): string {
  const p = new URLSearchParams();
  if (q.platform) p.set("platform", q.platform.id);
  if (q.gender) p.set("gender", q.gender);
  if (q.region) p.set("region", q.region);
  if (q.tag) p.set("tag", q.tag);
  if (q.isNew) p.set("new", "1");
  return p.toString();
}

/** A platform's live cams page (/cams/<id>), or the home page for every platform. */
export const camsPath = (platform: StreamPlatform | null) => (platform ? `/cams/${platform.id}` : "/");

/** Whether a path shows live streams (the home page or a platform's page): the left menu holds their filters there. */
export const isCamsPath = (pathname: string | null) => pathname === "/" || !!pathname?.startsWith("/cams/");

/** The live cams page for these filters: the platform is in the path, the rest in the query. */
export function streamsHref(q: StreamQuery): string {
  const params = streamParams({ ...q, platform: null });
  const path = camsPath(q.platform);
  return params ? `${path}?${params}` : path;
}

export const hasFilters = (q: StreamQuery) => Boolean(q.gender || q.region || q.tag || q.isNew);
